import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, ChevronDown, AlertCircle, FileText, LayoutTemplate, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ManageTemplatesSheet } from './manage-templates-sheet';
import { TemplateListPanel } from './template-list-panel';
import { ProjectForm } from './project-form';
import type { ProjectFormDraftData } from './project-form';
import { useProjectForm } from '@/providers/ProjectFormProvider';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { createProject, saveProjectCustomFieldValues, createCostCentre, saveProjectRoleAssignments, type CreateProjectData, type CreateCostCentreData } from '@/lib/data-service';
import { updateProjectBillingConfig, type ProjectBillingConfig } from '@/services/billingService';
import { useDataMode } from '@/contexts/DataModeContext';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { toast } from 'sonner';
import { useTemplates, type ProjectTemplate } from '@/contexts/TemplateContext';
import type { CompletionCalcMethod } from '@/types/template.types';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes';

interface ProjectCreationPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: 'create' | 'edit' | 'clone';
  projectId?: string;
  /** When set, the panel skips the gateway and opens straight to the form with this template pre-applied. */
  initialTemplateId?: string;
}

// Feature flag for AI Assist card — set to true to re-enable
const SHOW_AI_ASSIST: boolean = false;

export function ProjectCreationPanel({
  open,
  onOpenChange,
  mode = 'create',
  projectId,
  onProjectCreated,
  initialTemplateId,
}: ProjectCreationPanelProps & { onProjectCreated?: () => void }) {
  const { formState, minimizeForm, restoreForm, clearForm } = useProjectForm();
  const { isDemoMode } = useDataMode();
  const navigate = useNavigate();
  const { handleApiError } = useErrorHandler({ useToast: true });
  const { templates } = useTemplates();
  const { getStatusLabel } = useProjectStatuses();
  const [isSaving, setIsSaving] = useState(false);
  const [currentFormData, setCurrentFormData] = useState<ProjectFormDraftData | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string>(initialTemplateId ?? '');
  const [aiMode, setAiMode] = useState(false);
  const [manageTemplatesOpen, setManageTemplatesOpen] = useState(false);
  // In create mode, start on the gateway screen (showForm=false).
  // In edit/clone mode, or when opened via "Use Template" (initialTemplateId set), go straight to the form.
  const [showForm, setShowForm] = useState(mode !== 'create' || !!initialTemplateId);
  // Which gateway card is currently focused/selected
  const [creationMethod, setCreationMethod] = useState<'blank' | 'template' | 'ai'>(initialTemplateId ? 'template' : 'blank');
  // Template list screen — shown between gateway and form when creationMethod === 'template'
  const [showTemplateList, setShowTemplateList] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [formProgress, setFormProgress] = useState(0);
  const [incompleteFields, setIncompleteFields] = useState<string[]>([]);
  // Killer UX: Track whether to show full details or just Quick Create
  const [showFullDetails, setShowFullDetails] = useState(false);
  // Track which field to highlight
  const [highlightedField, setHighlightedField] = useState<string | null>(null);
  // Track dropdown open state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // Track where the user came from when entering AI mode (gateway or form)
  const [aiPreviousScreen, setAiPreviousScreen] = useState<'gateway' | 'form'>('gateway');
  // AI generation loading state
  const [isGenerating, setIsGenerating] = useState(false);
  // AI loading message (cycles through messages while generating)
  const [aiLoadingMessage, setAiLoadingMessage] = useState('');
  // AI generation error state
  const [aiError, setAiError] = useState<string | null>(null);
  // AI suggested data to pass to the form
  const [aiSuggestedData, setAiSuggestedData] = useState<import('./project-form').AiSuggestedData | null>(null);

  // Refs and method list for the 3 gateway cards — used for arrow-key focus management
  const GATEWAY_METHODS: Array<'blank' | 'template' | 'ai'> = ['blank', 'template', 'ai'];
  const gatewayCardRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // AI mode — single plain-English description input
  const [projectDescription, setProjectDescription] = useState('');

  // Restore form if reopening with a minimised state
  useEffect(() => {
    if (open) {
      if (formState && !formState.isMinimized && formState.hasUnsavedChanges) {
        // Form was just restored from a minimised state — go straight to the form.
        // Restored forms are always blank (template/AI paths don't minimise).
        setShowForm(true);
        setCreationMethod('blank');
      }
    }
  }, [open, formState]);

  // When the panel opens with an initialTemplateId, sync state in case it changed
  // (e.g. "Use Template" clicked on a different template while panel is already mounted)
  useEffect(() => {
    if (open && initialTemplateId) {
      setSelectedTemplate(initialTemplateId);
      setShowForm(true);
      setCreationMethod('template');
      setShowTemplateList(false);
    }
  }, [open, initialTemplateId]);

  const handleCancel = () => {
    // Reset state and close
    setSelectedTemplate('');
    setAiMode(false);
    setAiPreviousScreen('gateway');
    setIsGenerating(false);
    setAiLoadingMessage('');
    setAiError(null);
    setAiSuggestedData(null);
    // Return to gateway on next open (unless edit/clone — those skip the gateway)
    setShowForm(mode !== 'create');
    setShowTemplateList(false);
    setTemplateSearch('');
    setCreationMethod('blank');
    setShowFullDetails(false);
    setProjectDescription('');
    onOpenChange(false);
  };

  const handleFormSubmit= async (data: unknown) => {
    const formData = data as {
      projectName: string;
      projectCode?: string;
      description?: string;
      status: string;
      priority?: string;
      type: string;

      startDate: Date;
      endDate: Date;
      projectOwner?: string;
      projectManager: string;
      projectManagers?: string[];
      clients?: string;
      tags?: string[];
      projectCustomFields?: Record<string, string>;
      costCentres?: Array<{ name: string; code: string }>;
      roleAssignments?: Array<{ role: string; employeeId: string; employeeName: string }>;
      objectives?: string;
      keyDeliverables?: string;
      // Billing configuration copied from the selected template
      billable?: boolean;
      billingFrequency?: ProjectBillingConfig['billingFrequency'];
      billingTriggerType?: string;
      completionCalcMethod?: CompletionCalcMethod;
      invoiceTemplateId?: number | null;
      invoiceEmailTemplateId?: string | null;
      creditNoteEmailTemplateId?: string | null;
    };

    setIsSaving(true);

    // The Project Manager is captured in the Key People section, which submits
    // role assignments rather than a flat projectManager field. Fall back to the
    // project-manager assignment so projectManagerId is persisted on the project.
    const projectManagerFromRoles = formData.roleAssignments?.find(
      a => a.role === 'project-manager',
    )?.employeeId;
    const projectManager = formData.projectManager || projectManagerFromRoles;

    try {
      const projectData: CreateProjectData = {
        name: formData.projectName,
        projectCode: formData.projectCode,
        description: formData.description,
        statusId: Number(formData.status) as 1 | 2 | 3 | 4 | 5 | 6 | 7 || 1,
        priority: formData.priority,
        type: formData.type,

        startDate: formData.startDate,
        endDate: formData.endDate,
        projectOwner: formData.projectOwner,
        projectManager,
        projectManagers: formData.projectManagers,
        clients: formData.clients,
        tags: formData.tags,
        objectives: formData.objectives,
        keyDeliverables: formData.keyDeliverables,
        // billable is a Project-level field, persisted as part of the create payload
        ...(formData.billable !== undefined ? { billable: formData.billable } : {}),
      };

      const createdProject = await createProject(projectData);

      // Resolve the new project's id, preserving demo string ids (`demo-…`).
      // Real (live) projects have purely-numeric ids; only those are coerced to
      // a number. parseInt on a demo id would yield NaN and silently skip every
      // post-create side effect and the navigation below.
      const createdProjectId: number | string | undefined =
        createdProject?.id == null
          ? undefined
          : /^\d+$/.test(String(createdProject.id))
            ? Number(createdProject.id)
            : String(createdProject.id);

      // Save project attribute values if any were provided
      if (formData.projectCustomFields && Object.keys(formData.projectCustomFields).length > 0 && createdProjectId != null) {
        try {
          await saveProjectCustomFieldValues(createdProjectId, formData.projectCustomFields);
        } catch (attrError) {
          console.error('Failed to save project attributes:', attrError);
        }
      }

      // Persist billing configuration copied from the template. Billing config
      // lives in a separate ProjectBillingConfig table, so it needs its own
      // follow-up PUT after the project is created (billable is a Project-level
      // field and is already sent in the create payload above).
      // Skipped in demo mode: billingService always hits the live API and has no
      // mock path, so there is nothing to persist against a demo project.
      if (createdProjectId != null && !isDemoMode) {
        const billingUpdates: Partial<Omit<ProjectBillingConfig, 'projectId'>> = {};
        if (formData.billingFrequency !== undefined && formData.billingFrequency !== null) {
          billingUpdates.billingFrequency = formData.billingFrequency;
        }
        if (formData.billingTriggerType !== undefined && formData.billingTriggerType !== null) {
          billingUpdates.billingTriggerType = formData.billingTriggerType;
        }
        if (formData.invoiceTemplateId !== undefined && formData.invoiceTemplateId !== null) {
          billingUpdates.invoiceTemplateId = formData.invoiceTemplateId;
        }
        if (formData.invoiceEmailTemplateId !== undefined && formData.invoiceEmailTemplateId !== null) {
          billingUpdates.invoiceEmailTemplateId = formData.invoiceEmailTemplateId;
        }
        if (formData.creditNoteEmailTemplateId !== undefined && formData.creditNoteEmailTemplateId !== null) {
          billingUpdates.creditNoteEmailTemplateId = formData.creditNoteEmailTemplateId;
        }
        // The template/creation form calls this completionCalcMethod; on the project
        // it is the billing config's completionPercentMode (same value set).
        if (formData.completionCalcMethod) {
          billingUpdates.completionPercentMode = formData.completionCalcMethod;
        }

        if (Object.keys(billingUpdates).length > 0) {
          try {
            await updateProjectBillingConfig(createdProjectId, billingUpdates);
          } catch (billingError) {
            console.error('Failed to save project billing configuration:', billingError);
          }
        }
      }

      // Save role assignments (Key People) on the newly created project
      if (formData.roleAssignments && formData.roleAssignments.length > 0 && createdProjectId != null) {
        try {
          const assignmentItems = formData.roleAssignments.map(a => ({
            role: a.role,
            employeeId: typeof a.employeeId === 'string' ? parseInt(a.employeeId, 10) : a.employeeId,
            employeeName: a.employeeName,
          }));
          await saveProjectRoleAssignments(createdProjectId, assignmentItems);
        } catch (raError) {
          console.error('Failed to save role assignments:', raError);
        }
      }

      // Create cost centres from the template / form on the newly created project
      if (formData.costCentres && formData.costCentres.length > 0 && createdProjectId != null) {
        // Fall back to the project's selected status when a cost centre has no template status
        const selectedStatusId = Number(formData.status);
        const selectedProjectStatusLabel = isNaN(selectedStatusId) ? undefined : getStatusLabel(selectedStatusId);
        // Look up the full template cost centre data for richer fields (type, billable, status, budgets)
        const templateCostCentres = selectedTemplate
          ? templates.find(t => t.id === selectedTemplate)?.costCentres
          : undefined;

        for (const cc of formData.costCentres) {
          try {
            // Match template cost centre by code for additional fields
            const templateCc = templateCostCentres?.find(tc => tc.code === cc.code);
            const costCentreData: CreateCostCentreData = {
              projectId: createdProjectId,
              code: cc.code,
              name: cc.name,
              type: templateCc?.type,
              status: templateCc?.status ?? selectedProjectStatusLabel,
              billable: templateCc?.billable,
              costsBudget: templateCc?.costsBudget,
              revenueBudget: templateCc?.revenueBudget,
              hoursBudget: templateCc?.hoursBudget,
            };
            await createCostCentre(costCentreData);
          } catch (ccError) {
            console.error(`Failed to create cost centre '${cc.code}':`, ccError);
          }
        }
      }
      
      if (isDemoMode) {
        toast.success('Project created successfully (Demo Mode - changes are not persisted)');
      } else {
        toast.success('Project created successfully');
      }
      
      clearForm();
      onOpenChange(false);
      onProjectCreated?.();

      // Navigate to the newly created project's detail page
      if (createdProjectId != null) {
        navigate(ROUTES.PROJECT_DETAILS(createdProjectId));
      }
    } catch (error) {
      handleApiError(error, '/api/projects', 'POST');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFormCancel = () => {
    handleCancel();
    clearForm();
  };

  const handleSaveAsDraft = (formData?: ProjectFormDraftData) => {
    // Save form data as draft
    const dataToSave = formData || currentFormData;
    if (dataToSave) {
      minimizeForm(dataToSave);
    }
    onOpenChange(false);
  };

  const handleFieldClick= (fieldLabel: string) => {
    console.log('handleFieldClick called with:', fieldLabel);
    
    // Close the dropdown first
    setDropdownOpen(false);
    console.log('Dropdown closed');
    
    // Map field label back to field key for scrolling
    const fieldKeyMap: Record<string, string> = {
      'Project Name': 'projectName',
      'Project Code': 'projectCode',
      'Status': 'status',
      'Priority': 'priority',
      'Type': 'type',
      'Start Date': 'startDate',
      'End Date': 'endDate',
      'Project Manager': 'projectManager',
      'Description': 'description',
      'Clients': 'clients',
      'Tags': 'tags',
    };

    const fieldKey = fieldKeyMap[fieldLabel.replace(' *', '')];
    console.log('Field key mapped to:', fieldKey);
    
    if (fieldKey) {
      // Set highlighted field
      setHighlightedField(fieldKey);
      console.log('Highlighted field set to:', fieldKey);
      
      // Clear highlight after 1.5 seconds
      setTimeout(() => {
        setHighlightedField(null);
        console.log('Highlight cleared');
      }, 1500);
    } else {
      console.log('No field key found for label:', fieldLabel);
    }
  };

  const getModalTitle = () => {
    if (mode === 'edit') return 'Edit Project';
    if (mode === 'clone') return 'Clone Project';
    if (!showForm && !aiMode && !showTemplateList) return 'New Project';
    if (showTemplateList) return 'Choose a Template';
    return aiMode ? 'AI Project Creation' : 'Create New Project';
  };

  const getModalDescription = () => {
    if (!showForm && !aiMode && !showTemplateList) return 'Choose how you want to start your new project.';
    if (showTemplateList) return 'Select a template to pre-fill your project details.';
    if (showForm) return 'Fill in the project details below. All fields marked with * are required.';
    if (aiMode) return 'Describe your project and let AI create a comprehensive project plan for you.';
    if (mode === 'edit') return 'Update your project details and settings.';
    if (mode === 'clone') return 'Create a new project based on an existing one.';
    return '';
  };

  // Step indicator — gateway=1, template list / AI intake / form = 2
  const currentStep = (!showForm && !aiMode && !showTemplateList) ? 1 : 2;
  const totalSteps = 2;

  // Called when the user picks a template from the list
  const handleTemplateSelected = (templateId: string) => {
    setSelectedTemplate(templateId);
    setShowTemplateList(false);
    setShowForm(true);
  };

  // ── Mock canned AI responses (Task 4.6) ────────────────────────────────────
  // Keyword → pre-filled form values. The first matching response wins.
  // In a future phase, this will be replaced by a real POST /api/ai/project-suggest call.
  const CANNED_AI_RESPONSES: Array<{
    keywords: string[];
    response: import('./project-form').AiSuggestedData;
  }> = [
    {
      keywords: ['website', 'web', 'redesign', 'frontend', 'ui', 'ux', 'portal', 'digital'],
      response: {
        projectName: 'Website Redesign Project',
        description: 'A comprehensive website redesign to modernise the digital presence, improve user experience, and increase conversion rates. Includes responsive design, accessibility compliance, and CMS integration.',
        type: 'fixed-price',
        priority: 'High',

        startDate: new Date(new Date().setDate(new Date().getDate() + 14)),
        endDate: new Date(new Date().setMonth(new Date().getMonth() + 4)),
        costCentres: [
          { name: 'Design & UX', code: 'DES-001' },
          { name: 'Development', code: 'DEV-001' },
          { name: 'QA & Testing', code: 'QAT-001' },
        ],
        objectives: 'Modernise the website to reflect the current brand identity and improve user experience. Increase conversion rate by 20% and reduce bounce rate by 15%. Achieve WCAG 2.1 AA accessibility compliance.',
        keyDeliverables: 'Responsive website design with updated brand guidelines\nAccessibility-compliant HTML/CSS components\nCMS integration and content migration\nPerformance-optimised build with <2s load time\nUser acceptance testing report',
      },
    },
    {
      keywords: ['mobile', 'app', 'ios', 'android', 'react native', 'flutter'],
      response: {
        projectName: 'Mobile Application Development',
        description: 'Design and development of a cross-platform mobile application for iOS and Android. Features include user authentication, push notifications, offline mode, and integration with existing backend APIs.',
        type: 'time-and-materials',
        priority: 'High',

        startDate: new Date(new Date().setDate(new Date().getDate() + 7)),
        endDate: new Date(new Date().setMonth(new Date().getMonth() + 6)),
        costCentres: [
          { name: 'iOS Development', code: 'IOS-001' },
          { name: 'Android Development', code: 'AND-001' },
          { name: 'Backend Integration', code: 'BAK-001' },
        ],
        objectives: 'Deliver a high-quality cross-platform mobile app with a 4.5+ App Store rating. Support 10,000+ concurrent users at launch. Reduce customer support tickets by 30% through improved self-service features.',
        keyDeliverables: 'iOS and Android app builds submitted to app stores\nUser authentication and onboarding flows\nPush notification system\nOffline mode with data sync\nAPI integration documentation\nQA test results and sign-off',
      },
    },
    {
      keywords: ['marketing', 'campaign', 'brand', 'social media', 'seo', 'content', 'launch'],
      response: {
        projectName: 'Marketing Campaign',
        description: 'A multi-channel marketing campaign to increase brand awareness and drive customer acquisition. Includes social media, SEO, paid advertising, and content marketing workstreams.',
        type: 'fixed-price',
        priority: 'Medium',

        startDate: new Date(new Date().setDate(new Date().getDate() + 7)),
        endDate: new Date(new Date().setMonth(new Date().getMonth() + 3)),
        costCentres: [
          { name: 'Digital Media', code: 'DIG-001' },
          { name: 'Content Production', code: 'CON-001' },
        ],
        objectives: 'Increase brand awareness by 25% across target demographics. Generate 500 qualified leads per month by campaign end. Improve organic search ranking to top 3 for 10 priority keywords.',
        keyDeliverables: 'Multi-channel campaign strategy document\n12-week content calendar\nSEO-optimised landing pages\nPaid advertising creative assets\nWeekly performance dashboards\nEnd-of-campaign report with ROI analysis',
      },
    },
    {
      keywords: ['infrastructure', 'cloud', 'migration', 'aws', 'azure', 'devops', 'server', 'network'],
      response: {
        projectName: 'Infrastructure Upgrade',
        description: 'Cloud migration and infrastructure modernisation to improve scalability, security, and reduce operational costs. Includes assessment, migration planning, execution, and post-migration support.',
        type: 'time-and-materials',
        priority: 'Critical',

        startDate: new Date(new Date().setDate(new Date().getDate() + 21)),
        endDate: new Date(new Date().setMonth(new Date().getMonth() + 5)),
        costCentres: [
          { name: 'Infrastructure', code: 'INF-001' },
          { name: 'Security', code: 'SEC-001' },
          { name: 'Project Management', code: 'PMO-001' },
        ],
        objectives: 'Migrate all workloads to cloud with zero data loss and <4 hours total downtime. Reduce infrastructure operating costs by 30%. Achieve ISO 27001 security compliance post-migration.',
        keyDeliverables: 'Current-state infrastructure assessment report\nCloud architecture design and migration plan\nSecurity hardening and compliance documentation\nMigrated environments with rollback procedures\nPost-migration performance benchmarks\nHandover and operations runbook',
      },
    },
    {
      keywords: ['consulting', 'advisory', 'strategy', 'analysis', 'audit', 'review', 'assessment'],
      response: {
        projectName: 'Strategic Consulting Engagement',
        description: 'A structured consulting engagement to analyse current business processes, identify improvement opportunities, and deliver actionable recommendations with a clear implementation roadmap.',
        type: 'time-and-materials',
        priority: 'Medium',

        startDate: new Date(new Date().setDate(new Date().getDate() + 7)),
        endDate: new Date(new Date().setMonth(new Date().getMonth() + 2)),
        costCentres: [
          { name: 'Consulting', code: 'CON-001' },
          { name: 'Research & Analysis', code: 'RES-001' },
        ],
        objectives: 'Deliver a comprehensive analysis of current-state operations with measurable improvement opportunities. Provide a prioritised implementation roadmap with clear ROI projections. Ensure stakeholder alignment and executive buy-in on recommendations.',
        keyDeliverables: 'Current-state assessment and gap analysis\nStakeholder interview findings report\nProcess improvement recommendations\nPrioritised implementation roadmap\nROI and business case documentation\nExecutive presentation and sign-off',
      },
    },
  ];

  const AI_LOADING_MESSAGES = [
    'Analysing your project description…',
    'Identifying key project details…',
    'Determining project type and priority…',
    'Preparing your form…',
  ];

  const handleAiGenerate = async () => {
    if (!projectDescription.trim()) return;
    setIsGenerating(true);
    setAiError(null);
    setAiLoadingMessage(AI_LOADING_MESSAGES[0]);

    // Cycle through loading messages for UX polish
    let messageIndex = 0;
    const messageInterval = setInterval(() => {
      messageIndex = Math.min(messageIndex + 1, AI_LOADING_MESSAGES.length - 1);
      setAiLoadingMessage(AI_LOADING_MESSAGES[messageIndex]);
    }, 700);

    try {
      // Simulate AI processing delay (Task 4.2)
      await new Promise(resolve => setTimeout(resolve, 2800));

      // Find the best matching canned response (Task 4.6)
      const lowerDesc = projectDescription.toLowerCase();
      const matched = CANNED_AI_RESPONSES.find(({ keywords }) =>
        keywords.some(kw => lowerDesc.includes(kw))
      );

      const suggestion = matched?.response ?? {
        // Fallback: use the description as-is with sensible defaults
        description: projectDescription,
        type: 'time-and-materials',
        priority: 'Medium',
      };

      // Wire AI response to pre-fill the form (Task 4.4)
      setAiSuggestedData(suggestion);
      setAiMode(false);
      setShowForm(true);
    } catch {
      // Error / fallback state (Task 4.3) — show error, offer to continue with blank form
      setAiError('Something went wrong while processing your description. You can try again or continue with a blank form.');
    } finally {
      clearInterval(messageInterval);
      setIsGenerating(false);
      setAiLoadingMessage('');
    }
  };

  const handleGatewayConfirm = () => {
    if (creationMethod === 'blank') {
      setShowForm(true);
    } else if (creationMethod === 'ai') {
      setAiPreviousScreen('gateway');
      setAiMode(true);
    } else if (creationMethod === 'template') {
      setShowTemplateList(true);
    }
  };

  // Arrow-key + Enter keyboard navigation across the 3 gateway cards
  const handleGatewayKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        const next = (index + 1) % GATEWAY_METHODS.length;
        setCreationMethod(GATEWAY_METHODS[next]);
        gatewayCardRefs.current[next]?.focus();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = (index - 1 + GATEWAY_METHODS.length) % GATEWAY_METHODS.length;
        setCreationMethod(GATEWAY_METHODS[prev]);
        gatewayCardRefs.current[prev]?.focus();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        setCreationMethod(GATEWAY_METHODS[index]);
        handleGatewayConfirm();
      }
    },
    [handleGatewayConfirm, GATEWAY_METHODS],
  );

  return (
    <>
      <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) handleCancel(); else onOpenChange(true); }}>
        <SheetContent
          side="right"
          className="w-[640px] sm:max-w-[640px] flex flex-col p-0 gap-0"
        >
          <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <div className="flex items-start justify-between gap-4 pr-6">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <SheetTitle>{getModalTitle()}</SheetTitle>
                  {mode === 'create' && (
                    <span className="inline-flex items-center gap-1.5 shrink-0">
                      {Array.from({ length: totalSteps }, (_, i) => (
                        <span
                          key={i}
                          className={cn(
                            "block rounded-full transition-all duration-300",
                            i + 1 === currentStep
                              ? "w-5 h-2 bg-primary"
                              : i + 1 < currentStep
                              ? "w-2 h-2 bg-primary/40"
                              : "w-2 h-2 bg-muted-foreground/25"
                          )}
                        />
                      ))}
                    </span>
                  )}
                </div>
                <SheetDescription>{getModalDescription()}</SheetDescription>
              </div>
              {showForm && (
                <div className="flex items-center gap-4 shrink-0">
                  <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                    <DropdownMenuTrigger asChild>
                      <button className="w-48 space-y-1 cursor-pointer hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-md p-1" aria-label={`Form completion: ${formProgress}%${incompleteFields.length > 0 ? `. ${incompleteFields.length} incomplete field${incompleteFields.length !== 1 ? 's' : ''}` : ''}`}>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground flex items-center gap-1">
                            Form Completion
                            {incompleteFields.length > 0 && (
                              <ChevronDown className="h-3 w-3" />
                            )}
                          </span>
                          <span className={cn(
                            "font-semibold",
                            formProgress === 100 ? "text-success-600 dark:text-success-400" : "text-muted-foreground"
                          )}>
                            {formProgress}%
                          </span>
                        </div>
                        <Progress 
                          value={formProgress} 
                          className={cn(
                            "h-1.5 transition-all",
                            formProgress === 100 && "bg-success-100 dark:bg-success-950"
                          )}
                        />
                      </button>
                    </DropdownMenuTrigger>
                    {incompleteFields.length > 0 && (
                      <DropdownMenuContent align="end" className="w-64 p-0">
                        <div className="px-3 py-2 text-xs font-semibold text-muted-foreground border-b">
                          Incomplete Required Fields ({incompleteFields.length})
                        </div>
                        <ScrollArea className="h-[132px]">
                          <ScrollAreaViewport className="h-full max-h-[132px]">
                            <div className="p-1">
                              {incompleteFields.map((field, index) => (
                                <DropdownMenuItem 
                                  key={index}
                                  className="flex items-start gap-2 py-2 cursor-pointer focus:bg-accent hover:bg-accent"
                                  onClick={() => handleFieldClick(field)}
                                >
                                  <AlertCircle className="h-4 w-4 text-warning-500 shrink-0 mt-0.5" />
                                  <span className="text-sm">{field}</span>
                                </DropdownMenuItem>
                              ))}
                            </div>
                          </ScrollAreaViewport>
                          <ScrollAreaScrollbar orientation="vertical" />
                        </ScrollArea>
                      </DropdownMenuContent>
                    )}
                  </DropdownMenu>
                </div>
              )}
            </div>
          </SheetHeader>

          <ScrollArea className="flex-1 min-h-0">
            <ScrollAreaViewport className="h-full">
          <div className="px-6 py-6">

            {/* ── Gateway Screen ── */}
            {!showForm && !aiMode && !showTemplateList && (
              <div className="animate-in fade-in-0 slide-in-from-top-2 duration-300 flex flex-col items-center">
                {/* Method cards */}
                <div className={cn('grid gap-3 w-full', SHOW_AI_ASSIST ? 'grid-cols-3' : 'grid-cols-2 max-w-md')}>
                  {/* Blank Project */}
                  <button
                    type="button"
                    ref={el => { gatewayCardRefs.current[0] = el; }}
                    onClick={() => setCreationMethod('blank')}
                    onDoubleClick={() => { setCreationMethod('blank'); handleGatewayConfirm(); }}
                    onKeyDown={e => handleGatewayKeyDown(e, 0)}
                    className={cn(
                      'group flex flex-col items-center gap-3 rounded-xl border-2 p-4 text-center transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      creationMethod === 'blank'
                        ? 'border-primary bg-primary/5'
                        : 'border-border bg-card hover:border-primary/50 hover:bg-accent/50'
                    )}
                  >
                    <div className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-full transition-colors',
                      creationMethod === 'blank' ? 'bg-primary/10' : 'bg-muted group-hover:bg-primary/10'
                    )}>
                      <FileText className={cn('h-5 w-5', creationMethod === 'blank' ? 'text-primary' : 'text-muted-foreground group-hover:text-primary')} />
                    </div>
                    <div className="space-y-1">
                      <p className={cn('font-semibold', creationMethod === 'blank' ? 'text-primary' : 'text-foreground')}>
                        Blank Project
                      </p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Start from scratch and fill in the details yourself.
                      </p>
                    </div>
                    {creationMethod === 'blank' && (
                      <div className="h-1.5 w-1.5 rounded-full bg-primary mt-auto" />
                    )}
                  </button>

                  {/* From Template */}
                  <button
                    type="button"
                    ref={el => { gatewayCardRefs.current[1] = el; }}
                    onClick={() => setCreationMethod('template')}
                    onDoubleClick={() => { setCreationMethod('template'); handleGatewayConfirm(); }}
                    onKeyDown={e => handleGatewayKeyDown(e, 1)}
                    className={cn(
                      'group flex flex-col items-center gap-3 rounded-xl border-2 p-4 text-center transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      creationMethod === 'template'
                        ? 'border-primary bg-primary/5'
                        : 'border-border bg-card hover:border-primary/50 hover:bg-accent/50'
                    )}
                  >
                    <div className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-full transition-colors',
                      creationMethod === 'template' ? 'bg-primary/10' : 'bg-muted group-hover:bg-primary/10'
                    )}>
                      <LayoutTemplate className={cn('h-5 w-5', creationMethod === 'template' ? 'text-primary' : 'text-muted-foreground group-hover:text-primary')} />
                    </div>
                    <div className="space-y-1">
                      <p className={cn('font-semibold', creationMethod === 'template' ? 'text-primary' : 'text-foreground')}>
                        From Template
                      </p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Pick a saved template and start with fields pre-filled.
                      </p>
                    </div>
                    {creationMethod === 'template' && (
                      <div className="h-1.5 w-1.5 rounded-full bg-primary mt-auto" />
                    )}
                  </button>

                  {/* AI Assist — set to true to re-enable */}
                  {SHOW_AI_ASSIST && (
                  <button
                    type="button"
                    ref={el => { gatewayCardRefs.current[2] = el; }}
                    onClick={() => setCreationMethod('ai')}
                    onDoubleClick={() => { setCreationMethod('ai'); handleGatewayConfirm(); }}
                    onKeyDown={e => handleGatewayKeyDown(e, 2)}
                    className={cn(
                      'group flex flex-col items-center gap-3 rounded-xl border-2 p-4 text-center transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      creationMethod === 'ai'
                        ? 'border-primary bg-primary/5'
                        : 'border-border bg-card hover:border-primary/50 hover:bg-accent/50'
                    )}
                  >
                    <div className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-full transition-colors',
                      creationMethod === 'ai' ? 'bg-primary/10' : 'bg-muted group-hover:bg-primary/10'
                    )}>
                      <Sparkles className={cn('h-5 w-5', creationMethod === 'ai' ? 'text-primary' : 'text-muted-foreground group-hover:text-primary')} />
                    </div>
                    <div className="space-y-1">
                      <p className={cn('font-semibold', creationMethod === 'ai' ? 'text-primary' : 'text-foreground')}>
                        AI Assist
                      </p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Describe your project and let AI fill in the details.
                      </p>
                    </div>
                    {creationMethod === 'ai' && (
                      <div className="h-1.5 w-1.5 rounded-full bg-primary mt-auto" />
                    )}
                  </button>
                  )}
                </div>

                {/* Gateway footer */}
                <div className="flex justify-end gap-3 mt-6 w-full">
                  <Button variant="outline" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button onClick={handleGatewayConfirm}>
                    {creationMethod === 'blank' && 'Start Blank Project'}
                    {creationMethod === 'template' && 'Choose a Template'}
                    {creationMethod === 'ai' && 'Continue with AI'}
                  </Button>
                </div>
              </div>
            )}

            {/* Template List Screen */}
            {showTemplateList && !showForm && (
              <TemplateListPanel
                templates={templates}
                onSelect={handleTemplateSelected}
                onBack={() => {
                  setShowTemplateList(false);
                  setTemplateSearch('');
                }}
                onCancel={handleCancel}
              />
            )}

            {showForm && (
              <ProjectForm
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
                onMinimize={handleSaveAsDraft}
                isSubmitting={isSaving}
                initialData={formState?.formData}
                onProgressChange={setFormProgress}
                onIncompleteFieldsChange={setIncompleteFields}
                onFormDataChange={setCurrentFormData}
                quickCreateMode={creationMethod === 'blank'}
                showFullDetails={showFullDetails}
                onShowFullDetails={() => setShowFullDetails(true)}
                onHideFullDetails={() => setShowFullDetails(false)}
                templates={templates}
                onTemplateSelect={(templateId) => {
                  if (templateId === 'none') {
                    setSelectedTemplate('');
                  } else {
                    setSelectedTemplate(templateId);
                    // TODO: Pre-fill form with template data when backend is ready
                    console.log('Template selected:', templateId);
                  }
                }}
                selectedTemplate={selectedTemplate}
                onAiAssist={() => {
                  // Switch to AI intake — hide the form, show the AI screen
                  setShowForm(false);
                  setAiPreviousScreen('form');
                  setAiMode(true);
                  setCreationMethod('ai');
                }}
                highlightedField={highlightedField}
                onManageTemplates={() => setManageTemplatesOpen(true)}
                aiSuggestedData={aiSuggestedData}
                onAiSuggestedDataConsumed={() => setAiSuggestedData(null)}
              />
            )}

            {/* AI Mode — Intake Screen */}
            {aiMode && (
              <>
              <div className="space-y-5 animate-in slide-in-from-top-2 duration-300">

                {/* Loading state (Task 4.2) */}
                {isGenerating && (
                  <div className="flex flex-col items-center justify-center gap-4 py-10 animate-in fade-in-0 duration-300">
                    <div className="relative flex items-center justify-center">
                      <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <Sparkles className="h-7 w-7 text-primary animate-pulse" />
                      </div>
                      <div className="absolute inset-0 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
                    </div>
                    <div className="text-center space-y-1">
                      <p className="text-sm font-medium text-foreground">{aiLoadingMessage}</p>
                      <p className="text-xs text-muted-foreground">This will only take a moment</p>
                    </div>
                  </div>
                )}

                {/* Error state (Task 4.3) */}
                {!isGenerating && aiError && (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 animate-in fade-in-0 duration-300">
                    <div className="flex gap-3">
                      <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-destructive">AI processing failed</p>
                        <p className="text-xs text-muted-foreground">{aiError}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-1"
                          onClick={() => {
                            setAiError(null);
                            setAiMode(false);
                            setShowForm(true);
                          }}
                        >
                          Continue with blank form
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Input form — hidden while generating */}
                {!isGenerating && (
                  <>
                    {/* Plain-English description input (Task 4.1) */}
                    <div className="space-y-2">
                      <Label htmlFor="project-description" className="text-sm font-semibold">
                        Describe your project <span className="text-destructive">*</span>
                      </Label>
                      <Textarea
                        id="project-description"
                        placeholder="e.g. A 3-month website redesign for a retail client, fixed price, kicking off in February. We need a new homepage, product pages, and checkout flow."
                        value={projectDescription}
                        onChange={(e) => { setProjectDescription(e.target.value); setAiError(null); }}
                        className="min-h-32 resize-none"
                        disabled={isGenerating}
                        autoFocus
                      />
                      <p className="text-xs text-muted-foreground">
                        Include the project goal, type, rough timeline, and any key constraints. The more detail you give, the better the suggestions.
                      </p>
                    </div>

                    {/* Example prompts */}
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Try an example</p>
                      <div className="flex flex-wrap gap-2">
                        {[
                          'Cloud infrastructure migration, 5 months, T&M',
                          'Marketing campaign for product launch, Q2',
                          'Mobile app for internal team, iOS & Android',
                          'Strategic consulting review, 6 weeks',
                        ].map((example) => (
                          <button
                            key={example}
                            type="button"
                            onClick={() => { setProjectDescription(example); setAiError(null); }}
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 px-3 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                          >
                            {example}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* AI capability note */}
                    <div className="rounded-lg bg-primary/5 p-4 border border-primary/20">
                      <div className="flex gap-2.5">
                        <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-foreground mb-1">AI will suggest</p>
                          <p className="text-xs text-muted-foreground">Project name, description, type, priority, timeline and tags — all editable before you save.</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* AI Footer */}
              <div className="flex items-center justify-between pt-4 pb-2 border-t mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsGenerating(false);
                    setAiError(null);
                    setAiMode(false);
                    if (aiPreviousScreen === 'form') {
                      setShowForm(true);
                    }
                  }}
                  disabled={isGenerating}
                >
                  ← {aiPreviousScreen === 'form' ? 'Back to form' : 'Back to options'}
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                    disabled={isGenerating}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAiGenerate}
                    disabled={!projectDescription.trim() || isGenerating}
                    className="gap-2"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        Generate with AI
                      </>
                    )}
                  </Button>
                </div>
              </div>
              </>
            )}
          </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>

        </SheetContent>
      </Sheet>

      {/* Manage Templates Side-Drawer */}
      <ManageTemplatesSheet
        open={manageTemplatesOpen}
        onOpenChange={setManageTemplatesOpen}
      />
    </>
  );
}
